
import React from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { utils, writeFile } from "xlsx";
import { Lead } from "@/types/leads";
import LeadsLoading from "./LeadsLoading";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { FileDown } from "lucide-react";
import { toast } from "sonner";

interface LeadsListProps {
  leads: Lead[];
  isLoading: boolean;
  filter: {
    period: 'last7days' | 'last30days' | 'custom';
    startDate?: Date;
    endDate?: Date;
  };
  onRowClick: (leadId: string) => void;
}

const LeadsList: React.FC<LeadsListProps> = ({ 
  leads, 
  isLoading, 
  filter,
  onRowClick 
}) => {
  if (isLoading) {
    return <LeadsLoading />;
  }

  const exportToExcel = () => {
    try {
      if (leads.length === 0) {
        toast.error("Sem dados para exportar", {
          description: "Não há leads disponíveis para exportação"
        });
        return;
      }
      
      // Format data for export
      const excelData = leads.map(lead => ({
        Nome: lead.name,
        Email: lead.email,
        Telefone: lead.phone,
        "Página de Origem": lead.page || "N/A",
        "Data de Cadastro": format(
          new Date(lead.created_at),
          "dd/MM/yyyy HH:mm",
          { locale: ptBR }
        )
      }));
      
      // Create workbook and worksheet
      const worksheet = utils.json_to_sheet(excelData);
      const workbook = utils.book_new();
      utils.book_append_sheet(workbook, worksheet, "Leads");
      
      // Set column widths
      const columnWidths = [
        { wch: 25 }, // Nome
        { wch: 30 }, // Email
        { wch: 15 }, // Telefone
        { wch: 20 }, // Página
        { wch: 20 }  // Data
      ];
      worksheet["!cols"] = columnWidths;
      
      // Generate filename based on filter
      let filename = "leads_toctoc";
      if (filter.period === "last7days") {
        filename += "_ultimos7dias";
      } else if (filter.period === "last30days") {
        filename += "_ultimos30dias";
      } else if (filter.period === "custom" && filter.startDate && filter.endDate) {
        const startFormatted = format(filter.startDate, "yyyy-MM-dd");
        const endFormatted = format(filter.endDate, "yyyy-MM-dd");
        filename += `_${startFormatted}_ate_${endFormatted}`;
      }
      filename += ".xlsx";
      
      // Generate Excel file
      writeFile(workbook, filename);
      
      toast.success("Exportação concluída", {
        description: "Arquivo Excel gerado com sucesso"
      });
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error("Erro na exportação", {
        description: "Não foi possível gerar o arquivo Excel"
      });
    }
  };

  return (
    <div className="border rounded-md">
      {leads.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8">
          <p className="text-gray-500 mb-4">Nenhum lead encontrado</p>
          <Button 
            onClick={exportToExcel}
            variant="outline"
            className="text-gray-500"
            disabled
          >
            <FileDown className="mr-2 h-4 w-4" />
            Exportar Excel
          </Button>
        </div>
      ) : (
        <>
          <div className="p-4 border-b flex justify-between items-center">
            <div>
              <h3 className="font-medium">Total: {leads.length} leads</h3>
            </div>
            <Button 
              onClick={exportToExcel}
              className="bg-[#FF5605] hover:bg-[#e44e05] text-white"
              data-export-button
            >
              <FileDown className="mr-2 h-4 w-4" />
              Exportar Excel
            </Button>
          </div>
          
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>E-mail</TableHead>
                <TableHead>Telefone</TableHead>
                <TableHead>Página de Origem</TableHead>
                <TableHead>Data de Cadastro</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.map((lead) => (
                <TableRow 
                  key={lead.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => onRowClick(lead.id)}
                >
                  <TableCell className="font-medium">{lead.name}</TableCell>
                  <TableCell>{lead.email}</TableCell>
                  <TableCell>{lead.phone}</TableCell>
                  <TableCell>{lead.page || "N/A"}</TableCell>
                  <TableCell>
                    {format(
                      new Date(lead.created_at),
                      "dd/MM/yyyy HH:mm",
                      { locale: ptBR }
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </div>
  );
};

export default LeadsList;
